import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components";
import { useAuthSession } from "../../auth/shared";
import { useCurrentUserProfile } from "../hooks/useCurrentUserProfile";

interface AuthenticatedProfileBootstrapProps {
  children: React.ReactNode;
}

const AuthenticatedProfileBootstrap: React.FC<AuthenticatedProfileBootstrapProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearSession } = useAuthSession();
  const { error, isLoading, isReady, refetch } = useCurrentUserProfile();

  const handleRetry = useCallback(() => {}, [refetch]);

  const handleLogout = useCallback(() => {
    clearSession();
    navigate("/login", { replace: true });
  }, [clearSession, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center max-w-md px-6">
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t("ui.something_went_wrong")}
          </p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{error}</p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button
              type="button"
              onClick={handleRetry}
            >
              {t("ui.retry")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleLogout}
            >
              {t("common.logout")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!isReady || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthenticatedProfileBootstrap;
